import { MdArrowBack, MdMedicalServices } from 'react-icons/md'
import { ServiceType } from '../types'

interface SchedulingHeaderProps {
    isEditing: boolean
    isBudgetMode: boolean
    serviceType: ServiceType
    /** Rótulos por segmento — fora da saúde, Procedimento vira Pedido. */
    serviceTypeLabels: Record<ServiceType, string>
    /** Tipos que o usuário vê no menu; os demais não aparecem no seletor. */
    availableServiceTypes: ServiceType[]
    onBack: () => void
    onServiceTypeChange: (type: ServiceType) => void
}

/**
 * Cabeçalho da tela de agendamento: voltar, título e a troca de tipo de serviço.
 *
 * O seletor de tipo mora no cabeçalho — e não como seção do formulário — porque
 * trocar o tipo troca o formulário inteiro; ele não concorre com o índice lateral,
 * que é vertical e com ícones. Em edição o tipo deixa de ser escolha e vira
 * contexto (um selo), em vez de sumir da tela.
 */
export default function SchedulingHeader({
    isEditing,
    isBudgetMode,
    serviceType,
    serviceTypeLabels,
    availableServiceTypes,
    onBack,
    onServiceTypeChange,
}: SchedulingHeaderProps) {
    return (
        <header className="relative overflow-hidden rounded-2xl border border-base-300 bg-base-100">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-linear-to-r from-secondary/10 via-primary/5 to-transparent"
            />
            <div className="relative flex items-center gap-4 p-5">
                <button
                    type="button"
                    onClick={onBack}
                    className="btn btn-ghost btn-circle btn-sm shrink-0"
                    title="Voltar"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary ring-1 ring-secondary/20">
                    <MdMedicalServices className="text-2xl" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-base-content/40">
                        {isEditing ? 'Editando' : 'Novo cadastro'}
                    </p>
                    <h1 className="truncate text-xl font-bold text-base-content sm:text-2xl">
                        {isEditing
                            ? isBudgetMode
                                ? `Orçamento de ${serviceTypeLabels[serviceType]}`
                                : serviceTypeLabels[serviceType]
                            : isBudgetMode
                              ? 'Novo orçamento'
                              : 'Novo agendamento'}
                    </h1>
                </div>
                {isEditing && (
                    <span className="hidden shrink-0 items-center gap-2 rounded-full border border-base-300 bg-base-100 px-3 py-1.5 text-xs font-medium text-base-content/70 sm:inline-flex">
                        {serviceTypeLabels[serviceType]}
                    </span>
                )}
            </div>

            {!isEditing && (
                <div className="relative border-t border-base-300 px-5 py-3">
                    <div className="inline-flex flex-wrap gap-1 rounded-xl bg-base-200/70 p-1">
                        {availableServiceTypes.map(type => {
                            const active = serviceType === type
                            return (
                                <button
                                    key={type}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => onServiceTypeChange(type)}
                                    className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                                        active
                                            ? 'bg-base-100 text-base-content shadow-sm ring-1 ring-secondary/30'
                                            : 'text-base-content/50 hover:text-base-content'
                                    }`}
                                >
                                    {serviceTypeLabels[type]}
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}
        </header>
    )
}
